/* ═══════════════════════════════════════════════════════════════════════════
   HERO — scroll-driven frame sequence on a pinned canvas (Apple-style).

   Two render modes (Theme settings → Hero frame sequence):
     "placeholder"  procedurally draws a gradient bottle → brush → nail scene,
                    so the scroll feel can be previewed with zero assets.
     "frames"       preloads N WebP frames (desktop 1920 / mobile 960 set) with a
                    progress ring and draws the frame for the scroll position.

   Scrolling forward plays the sequence, scrolling back reverses it (scrub).
   Text beats fade in at 0 / 30 / 60 / 90 %. A Skip control jumps to the end.
   prefers-reduced-motion → the final frame + all text, no pinning.
   ═══════════════════════════════════════════════════════════════════════════ */
(function () {
  "use strict";

  var section = document.querySelector("[data-hero]");
  if (!section) return;

  var cfg = {
    mode: section.dataset.mode || "placeholder",
    count: Math.max(2, parseInt(section.dataset.count || "150", 10)),
    desktopUrl: section.dataset.desktopUrl || "",
    mobileUrl: section.dataset.mobileUrl || "",
    breakpoint: parseInt(section.dataset.breakpoint || "768", 10),
    accent: getComputedStyle(document.documentElement).getPropertyValue("--accent").trim() || "#C9A79C",
    paper: "#F7F4EF",
    ink: "#141311"
  };
  if (cfg.mode === "frames" && !cfg.desktopUrl) cfg.mode = "placeholder";

  var stage = section.querySelector(".nl-hero__stage");
  var canvas = section.querySelector("canvas");
  // alpha:true so the poster image underneath shows through until a frame is drawn.
  var ctx = canvas.getContext("2d");
  var beats = Array.prototype.slice.call(section.querySelectorAll("[data-beat]")).map(function (el) {
    return {
      el: el,
      at: parseFloat(el.dataset.beat),
      out: el.dataset.beatOut ? parseFloat(el.dataset.beatOut) : null,
      offset: parseFloat(el.dataset.offset || "0")
    };
  });
  var loader = section.querySelector(".nl-hero__loader");
  var loaderBar = loader && loader.querySelector(".bar");
  var loaderText = loader && loader.querySelector("span");
  var skip = section.querySelector(".nl-hero__skip");

  var isSmall = window.innerWidth < cfg.breakpoint;
  var DPR = Math.min(window.devicePixelRatio || 1, isSmall ? 1.5 : 2); // cap for phone GPUs
  var W = 0, H = 0;
  var progress = 0;
  var frames = [];         // HTMLImageElement[] (frames mode)
  var loaded = 0;
  var frameSet = null;

  /* ── Canvas sizing ──────────────────────────────────────────────────────── */
  var lastW = 0;
  function resize() {
    // Phones fire resize when the URL bar collapses; ignore height-only changes so the
    // canvas doesn't flash/re-layout mid-scroll.
    if (lastW && stage.clientWidth === lastW && Math.abs(stage.clientHeight - H) < 120) return;
    lastW = stage.clientWidth;
    W = stage.clientWidth;
    H = stage.clientHeight;
    canvas.width = Math.round(W * DPR);
    canvas.height = Math.round(H * DPR);
    canvas.style.width = W + "px";
    canvas.style.height = H + "px";
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    render(progress, true);
  }

  /* ── Frames mode: preload with progress ─────────────────────────────────── */
  function frameUrl(i) {
    var idx = String(i + 1);
    while (idx.length < 4) idx = "0" + idx;
    return frameSet.replace("{i}", idx);
  }
  function pickSet() {
    var mobile = window.innerWidth < cfg.breakpoint && cfg.mobileUrl;
    return mobile ? cfg.mobileUrl : cfg.desktopUrl;
  }
  function preload(onFirst) {
    frameSet = pickSet();
    frames = new Array(cfg.count);
    loaded = 0;
    if (loader) loader.classList.add("is-visible");
    // Load the first frame immediately, then the rest in a few parallel lanes.
    var order = [];
    for (var i = 0; i < cfg.count; i++) order.push(i);
    var lanes = 6, cursor = 0;
    function next() {
      if (cursor >= order.length) return;
      var i = order[cursor++];
      var img = new Image();
      img.decoding = "async";
      img.onload = img.onerror = function () {
        frames[i] = img.complete && img.naturalWidth ? img : null;
        // Ask the browser to decode off the main thread now, so the first draw of
        // each frame during scroll isn't a synchronous decode.
        if (frames[i] && img.decode) img.decode().catch(function () {});
        loaded++;
        if (loaderBar) loaderBar.style.strokeDashoffset = String(63 - 63 * (loaded / cfg.count));
        if (loaderText) loaderText.textContent = "Loading " + Math.round((loaded / cfg.count) * 100) + "%";
        if (loaded === 1 && onFirst) onFirst();
        if (loaded >= cfg.count && loader) loader.classList.remove("is-visible");
        render(progress);
        next();
      };
      img.src = frameUrl(i);
    }
    for (var l = 0; l < lanes; l++) next();
  }
  function nearestFrame(i) {
    // Fall back to the closest frame that has finished loading so it never stalls.
    if (frames[i]) return frames[i];
    for (var d = 1; d < cfg.count; d++) {
      if (frames[i - d]) return frames[i - d];
      if (frames[i + d]) return frames[i + d];
    }
    return null;
  }
  function drawCover(img) {
    var iw = img.naturalWidth, ih = img.naturalHeight;
    var s = Math.max(W / iw, H / ih);
    var dw = iw * s, dh = ih * s;
    ctx.drawImage(img, (W - dw) / 2, (H - dh) / 2, dw, dh);
  }

  /* ── Placeholder mode: procedural bottle → brush → nail scene ───────────── */
  function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }
  function ease(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }
  function seg(p, a, b) { return ease(clamp((p - a) / (b - a), 0, 1)); }
  function hex2rgb(h) { var n = parseInt(h.slice(1), 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; }
  function rgba(h, a) { var c = hex2rgb(h); return "rgba(" + c[0] + "," + c[1] + "," + c[2] + "," + a + ")"; }

  function roundRect(x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }
  function nailPath(cx, cy, w, h) {
    // Almond nail silhouette
    ctx.beginPath();
    ctx.moveTo(cx - w / 2, cy + h / 2);
    ctx.bezierCurveTo(cx - w / 2, cy - h * 0.15, cx - w * 0.28, cy - h / 2, cx, cy - h / 2);
    ctx.bezierCurveTo(cx + w * 0.28, cy - h / 2, cx + w / 2, cy - h * 0.15, cx + w / 2, cy + h / 2);
    ctx.closePath();
  }

  function drawPlaceholder(p) {
    var mobile = W < cfg.breakpoint;
    var u = Math.min(W, H); // scale unit

    // Background: soft paper gradient with a drifting accent glow
    var bg = ctx.createLinearGradient(0, 0, 0, H);
    bg.addColorStop(0, "#f9f7f3");
    bg.addColorStop(1, "#efe9e0");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);
    var gx = W * (0.35 + 0.3 * p), gy = H * (0.6 - 0.25 * p);
    var glow = ctx.createRadialGradient(gx, gy, 0, gx, gy, u * 0.75);
    glow.addColorStop(0, rgba(cfg.accent, 0.55));
    glow.addColorStop(1, rgba(cfg.accent, 0));
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, W, H);

    // Scene timing
    var twist = seg(p, 0.02, 0.3);   // cap twists + rises
    var lift = seg(p, 0.3, 0.55);    // brush comes out, bottle drifts
    var paint = seg(p, 0.55, 0.85);  // nail gets painted
    var gloss = seg(p, 0.85, 1.0);   // final glossy shine

    // Bottle geometry
    var bw = u * (mobile ? 0.22 : 0.16), bh = bw * 1.55;
    var bx = W / 2 - bw / 2 - lift * (mobile ? u * 0.28 : u * 0.42);
    var by = H * 0.5 - bh * 0.15 + lift * u * 0.06;

    ctx.save();
    ctx.globalAlpha = 1 - gloss * 0.85;
    // shadow
    ctx.fillStyle = "rgba(20,19,17,0.10)";
    ctx.beginPath(); ctx.ellipse(bx + bw / 2, by + bh + u * 0.02, bw * 0.75, u * 0.018, 0, 0, Math.PI * 2); ctx.fill();
    // glass body
    var body = ctx.createLinearGradient(bx, 0, bx + bw, 0);
    body.addColorStop(0, rgba(cfg.accent, 0.95));
    body.addColorStop(0.45, "#d9b9ae");
    body.addColorStop(1, "#b48c80");
    ctx.fillStyle = body;
    roundRect(bx, by, bw, bh, bw * 0.22); ctx.fill();
    // highlight strip
    ctx.fillStyle = "rgba(255,255,255,0.35)";
    roundRect(bx + bw * 0.14, by + bh * 0.08, bw * 0.12, bh * 0.8, bw * 0.06); ctx.fill();

    // Cap (twists then rises with the brush stem)
    var capW = bw * 0.62, capH = bh * 0.55;
    var capX = bx + bw / 2 - capW / 2;
    var capRise = twist * u * 0.05 + lift * u * (mobile ? 0.36 : 0.42);
    var capY = by - capH * 0.92 - capRise;
    var capDrift = lift * (mobile ? u * 0.45 : u * 0.62); // moves right toward the nail
    ctx.save();
    ctx.translate(capX + capW / 2 + capDrift, capY + capH / 2);
    ctx.rotate(twist * Math.PI * 0.9 - lift * Math.PI * 0.35);
    var capG = ctx.createLinearGradient(-capW / 2, 0, capW / 2, 0);
    capG.addColorStop(0, "#2a2724"); capG.addColorStop(0.5, "#141311"); capG.addColorStop(1, "#3a3531");
    ctx.fillStyle = capG;
    roundRect(-capW / 2, -capH / 2, capW, capH, capW * 0.18); ctx.fill();
    // brush stem + bristles
    var stemLen = bh * 0.9 * (0.25 + 0.75 * lift);
    ctx.fillStyle = "#1c1a18";
    roundRect(-capW * 0.06, capH / 2, capW * 0.12, stemLen, capW * 0.05); ctx.fill();
    var bristleG = ctx.createLinearGradient(0, capH / 2 + stemLen, 0, capH / 2 + stemLen + bh * 0.22);
    bristleG.addColorStop(0, "#b48c80"); bristleG.addColorStop(1, rgba(cfg.accent, 1));
    ctx.fillStyle = bristleG;
    roundRect(-capW * 0.12, capH / 2 + stemLen, capW * 0.24, bh * 0.22, capW * 0.1); ctx.fill();
    ctx.restore();
    ctx.restore();

    // Nail (appears, gets painted base→tip, then shines)
    var nailIn = seg(p, 0.5, 0.62);
    if (nailIn > 0) {
      var nw = u * (mobile ? 0.34 : 0.26) * (1 + gloss * 0.12);
      var nh = nw * 1.45;
      var ncx = W / 2 + (mobile ? u * 0.12 : u * 0.28) * (1 - gloss) ;
      var ncy = H * 0.52;
      ctx.save();
      ctx.globalAlpha = nailIn;
      // base nail
      nailPath(ncx, ncy, nw, nh);
      ctx.fillStyle = "#f2e6df";
      ctx.shadowColor = "rgba(20,19,17,0.18)"; ctx.shadowBlur = u * 0.05; ctx.shadowOffsetY = u * 0.01;
      ctx.fill();
      ctx.shadowColor = "transparent";
      // paint fill grows from base to tip
      ctx.save();
      nailPath(ncx, ncy, nw, nh); ctx.clip();
      var fillH = nh * paint;
      var paintG = ctx.createLinearGradient(ncx - nw / 2, 0, ncx + nw / 2, 0);
      paintG.addColorStop(0, "#b48c80"); paintG.addColorStop(0.5, rgba(cfg.accent, 1)); paintG.addColorStop(1, "#d9b9ae");
      ctx.fillStyle = paintG;
      ctx.fillRect(ncx - nw, ncy + nh / 2 - fillH, nw * 2, fillH + 2);
      // gloss sweep
      if (gloss > 0) {
        var sx = ncx - nw + gloss * nw * 2.2;
        var sh = ctx.createLinearGradient(sx - nw * 0.35, 0, sx + nw * 0.35, 0);
        sh.addColorStop(0, "rgba(255,255,255,0)"); sh.addColorStop(0.5, "rgba(255,255,255,0.6)"); sh.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = sh;
        ctx.fillRect(ncx - nw, ncy - nh, nw * 2, nh * 2);
      }
      // static highlight
      ctx.fillStyle = "rgba(255,255,255,0.32)";
      ctx.beginPath(); ctx.ellipse(ncx - nw * 0.22, ncy - nh * 0.1, nw * 0.07, nh * 0.28, 0.15, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
      ctx.restore();
    }

    // Fine grain overlay for a photographic feel (cheap: a few translucent lines)
    ctx.fillStyle = "rgba(20,19,17,0.025)";
    for (var y = 0; y < H; y += 3) ctx.fillRect(0, y, W, 1);
  }

  /* ── Render dispatcher ──────────────────────────────────────────────────── */
  var drawnFrame = -1, drawnImg = null;
  function render(p, force) {
    progress = p;
    if (cfg.mode === "frames") {
      var i = Math.round(p * (cfg.count - 1));
      var img = nearestFrame(i);
      // Only touch the canvas when the frame actually changes. On phones every
      // scroll tick used to redraw the same bitmap, which made touch scrolling
      // feel heavy.
      if (img && (force || i !== drawnFrame || img !== drawnImg)) {
        drawCover(img);
        drawnFrame = i; drawnImg = img;
      } else if (!img) {
        ctx.clearRect(0, 0, W, H); // nothing loaded yet → poster shows through
      }
    } else {
      drawPlaceholder(p);
    }
    updateBeats(p);
    if (skip) skip.classList.toggle("is-hidden", p > 0.96);
  }

  /* ── Text beats ─────────────────────────────────────────────────────────── */
  function updateBeats(p) {
    var fade = 0.06;
    beats.forEach(function (b) {
      // Beats at 0% are visible immediately; later beats fade in over `fade`.
      var a = b.at <= 0 ? 1 : clamp((p - b.at) / fade, 0, 1);
      if (b.out !== null) a *= 1 - clamp((p - b.out) / fade, 0, 1); // fade out
      a = Math.round(ease(a) * 100) / 100;
      if (a === b.last) return; // skip style writes when nothing changed
      b.last = a;
      b.el.style.opacity = String(a);
      b.el.style.transform = "translateY(" + (b.offset + (1 - a) * 14) + "px)";
    });
  }

  /* ── Public init (called by nl.js once GSAP/Lenis state is known) ───────── */
  window.NLHero = {
    init: function (opts) {
      var reduce = opts && opts.reduce;
      resize();
      window.addEventListener("resize", resize);

      if (reduce) {
        // Static: final frame + all text, no scroll linkage.
        beats.forEach(function (b) { b.el.style.opacity = "1"; b.el.style.transform = "none"; });
        if (cfg.mode === "frames") {
          frameSet = pickSet();
          var last = new Image();
          last.onload = function () { frames[cfg.count - 1] = last; render(1); };
          last.src = frameUrl(cfg.count - 1);
        } else {
          drawPlaceholder(1);
        }
        return;
      }

      if (cfg.mode === "frames") preload(function () { render(progress); });
      else render(0);

      // Scrub: scroll position ↔ sequence position (forward plays, back reverses).
      // Touch devices get a direct 1:1 scrub. The smoothed scrub (0.4s catch-up)
      // feels like drag/resistance under a finger; it stays on for mouse wheels.
      var touch = window.matchMedia("(hover: none) and (pointer: coarse)").matches || (navigator.maxTouchPoints || 0) > 0 || window.innerWidth < 900;
      ScrollTrigger.create({
        trigger: section,
        start: "top top",
        end: "bottom bottom",
        scrub: touch ? true : 0.4,
        onUpdate: function (self) { render(self.progress); }
      });

      // Skip → jump to the end of the sequence.
      if (skip) {
        skip.addEventListener("click", function () {
          var y = section.offsetTop + section.offsetHeight - window.innerHeight;
          if (opts && opts.lenis) opts.lenis.scrollTo(y, { duration: 1.2 });
          else window.scrollTo({ top: y, behavior: "smooth" });
        });
      }
    }
  };
})();
